import React from "react";

import { useSiteMetadata } from "../../../hooks/useSiteMetadata";

import { Link } from "../../../utils/customLink";

import Image from "../../../components/image";

import ArrowRight from "../../../images/icons/arrow-right.inline.svg";
import DiplomaIcon from "../../../images/icons/fi-rr-diploma.inline.svg";
import ArrowRightBigIcon from "../../../images/icons/arrow-right-big-icon.inline.svg";
import GoogleScholarLogo from "../../../images/icons/google-scholar-logo.svg";
import GoogleScholarLight from "../../../images/icons/google-scholar-logo-light.svg";
import GoogleScholar from "../../../images/icons/google-scholar-ico.png";

const ArticleBox = ({
  title,
  type,
  date,
  authors,
  summary,
  articleLink = "#"
}) => (
  <div className="article-box-container mt-4 md:p-7 bg-white planet:gvi-light-gray-bg rounded-xs overflow-hidden">
    <div className="article-box grid article-box-mobile md:article-box-desktop">
      <div className="box-title p-5 md:p-0 flex justify-start">
        <DiplomaIcon className="hidden md:block shrink-0 w-10 pr-2 fill-yellow-lighten planet:fill-white" />
        <h6
          className="heading text-left font-normal planet:font-light !pb-0"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>

      {type && (
        <div
          className="box-type mr-1 md:mr-0 md:mt-5 !py-[10px] md:rounded-xxs text-black-lighter planet:text-white text-center bg-yellow-lighter planet:bg-black-light"
          dangerouslySetInnerHTML={{ __html: type }}
        />
      )}

      {date && (
        <div
          className="box-date !py-[10px] md:rounded-xxs md:mt-2 text-black-lighter planet:text-white text-center bg-yellow-lighter planet:bg-black-light"
          dangerouslySetInnerHTML={{ __html: date }}
        />
      )}
      {summary && (
        <div
          className="box-summary px-5 !pb-0 md:px-0 md:!pt-3 text-gray-generic"
          dangerouslySetInnerHTML={{ __html: summary }}
        />
      )}

      {authors && (
        <div className="box-authors row-span-6 px-5 pt-3 md:pl-5 md:pr-0">
          <div className="text-gray-generic !pb-2">Author(s)</div>
          <div
            className="text-black-lighter planet:text-white !pb-0"
            dangerouslySetInnerHTML={{ __html: authors }}
          />
        </div>
      )}
    </div>
    {articleLink && (
      <div className="pt-5 pb-8 px-5 md:px-0 md:pb-2">
        <Link
          className="flex !justify-center sm:justify-between items-center w-full  sm:w-auto md:w-[45%] py-2 px-5 planet:px-4 text-mob sm:text-base font-bold !text-black-lighter planet:!text-white   border border-black-lighter planet:border-white rounded-xxxxl planet:rounded-sm text-generic-tex visited:text-black-lighter hover:border-0 hover:bg-black-lighter hover:!text-white planet:hover:bg-white planet:hover:border-0 planet:hover:!text-black-lighter group"
          to={articleLink}
        >
          Read article
          <span className="flex items-center pl-2">
            <ArrowRightBigIcon className="planet:hidden fill-black-lighter group-hover:fill-white" />
            <ArrowRight className="hidden planet:block stroke-white planet:group-hover:stroke-black-lighter" />
          </span>
        </Link>
      </div>
    )}
  </div>
);

const Publications = ({ publicationList }) => {
  const tabTitle = publicationList?.title ?? "Publications";
  const tabContent =
    publicationList?.content ??
    `The best decisions in international development and conservation cannot be made without accurate and up-to-date data or informed research.<br/> <br/>
  Our many field teams around the world collaborate with local and international partners to analyse data and draw conclusions. In addition, many of our participants have used research they have collected on their various GVI projects to complete their Masters, Doctorate, or postdoctoral studies. <br/> <br/>
  We also run a fellowship program which connects postdoctoral researchers at globally-respected universities with our many sustainable development programs around the world to support their research and ensure continuous improvement of our best practices on base.`;
  const articleData = publicationList?.list?.map((article) => ({
    title: article?.optPublicationTitle ?? "",
    type: article?.optPublicationType ?? "",
    date: article?.optPublicationYear ?? "",
    authors: article?.optPublicationAuthors ?? "",
    summary: article?.optPublicationText ?? "",
    articleLink: article?.optPublicationLink ?? ""
  }));

  const theMetadata = useSiteMetadata();
  const socialmedia = theMetadata?.siteMetadata?.social_media;

  return (
    <>
      <h2 className="heading text-left py-5">{tabTitle}</h2>
      <div className="divide-y width-divider" />

      <h6
        className="pt-6 font-body !font-normal planet:gvi-light-gray-text text-black-lighter tab-content_html"
        dangerouslySetInnerHTML={{ __html: tabContent }}
      />

      <div className="flex flex-col justify-between relative w-10/12 md:w-full mx-auto my-8 px-1 md:px-3 md:ml-1 gvi-border-light gvi-border-05 rounded-xxs flex-row">
        <div
          className={`w-9 h-9 absolute z-0 -left-4 -top-4 bg-white planet:gvi-dark-gray-bg  `}
        />
        <div
          className={`w-7 h-7 absolute z-0 -top-4 -left-4 bg-cover bg-no-repeat bg-center `}
          style={{
            backgroundImage: `url('${GoogleScholar}')`
          }}
        />

        <div className="flex flex-col md:flex-row items-center pt-4">
          <h6
            className="font-body text-[10px] md:text-[16px] !font-normal planet:gvi-light-gray-text text-black-lighter tab-content_html"
            dangerouslySetInnerHTML={{
              __html: "All of our publications are on Google Scholar "
            }}
          />
          <Image
            imageAlt={"Google Scholar"}
            image={""}
            imageClass={`w-24 md:w-32 h-auto pl-1 mt-1 hidden planet:block`}
            sourceUrl={GoogleScholarLogo}
          />
          <Image
            imageAlt={"Google Scholar"}
            image={""}
            imageClass={`w-24 md:w-32 h-auto pl-1 mt-1 block planet:hidden`}
            sourceUrl={GoogleScholarLight}
          />
        </div>
        <Link
          className="text-xs text-black-lighter planet:text-white my-auto pb-4 underline hover:text-white text-center md:text-left"
          to={socialmedia?.google_scholar}
          title={"View Google Scholar publications"}
          target={"_gviblank"}
          rel={"nofollow"}
        >
          View publications
        </Link>
      </div>

      {articleData &&
        articleData.length > 0 &&
        articleData.map((data, index) => <ArticleBox {...data} key={index} />)}
    </>
  );
};

export default Publications;
